// Editors for branding: the platform's own (Superadmin) and a company's (its admin, or the Superadmin).
// Both show a live preview of the login screen as it will look.
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Loader2, RotateCcw, Save, Upload, X } from "lucide-react";
import {
  DEFAULT_PLATFORM,
  DEFAULT_THEME,
  PLATFORM_BACKGROUND,
  PLATFORM_THEME,
  contrastRatio,
  isHex,
  mixHex,
  readableOn,
  luminance,
  saveTenantBranding,
  savePlatformBranding,
  uploadBrandingAsset,
  type PlatformBranding,
  type TenantBranding,
} from "@/lib/branding";

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const ImageField: React.FC<{
  label: string;
  hint?: string;
  value: string | null;
  folder: string;
  onChange: (url: string | null) => void;
}> = ({ label, hint, value, folder, onChange }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Choose an image file", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      onChange(await uploadBrandingAsset(file, folder));
    } catch (e: any) {
      toast({ title: "Couldn't upload", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex items-center gap-3">
        <div className="h-12 w-12 rounded border border-border bg-black/30 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={value} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-muted-foreground">none</span>}
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
          {value ? "Replace" : "Upload"}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            <X className="h-4 w-4 mr-1" /> Remove
          </Button>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
};

const ColourField: React.FC<{ label: string; value: string; onChange: (hex: string) => void }> = ({ label, value, onChange }) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={isHex(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-12 rounded border border-border bg-transparent p-1 cursor-pointer"
          aria-label={label}
        />
        <Input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (isHex(e.target.value)) onChange(e.target.value.toUpperCase());
          }}
          className="w-28 font-mono"
          maxLength={7}
          aria-label={`${label} (hex)`}
        />
      </div>
    </div>
  );
};

/** A small picture of the login screen with the chosen name / logo / colours. */
const LoginPreview: React.FC<{
  title: string;
  subtitle: string | null;
  tagline?: string | null;
  logoUrl: string | null;
  primary: string;
  secondary: string;
}> = ({ title, subtitle, tagline, logoUrl, primary, secondary }) => {
  return (
    <div
      className="rounded-lg p-5 flex items-center justify-center"
      style={{ background: `linear-gradient(135deg, ${PLATFORM_BACKGROUND.top}, ${PLATFORM_BACKGROUND.bottom})` }}
      aria-label="Login screen preview"
    >
      <div
        className="w-full max-w-xs rounded-lg p-5 text-center space-y-2"
        style={{
          background: PLATFORM_BACKGROUND.panel,
          border: `1px solid ${primary}44`,
        }}
      >
        {logoUrl && <img src={logoUrl} alt="" className="mx-auto max-h-12 max-w-[160px] object-contain" />}
        <div
          className="text-2xl font-bold"
          style={{ backgroundImage: `linear-gradient(135deg, ${primary}, ${secondary})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
        >
          {title || "Company name"}
        </div>
        {subtitle && <div className="text-sm font-medium" style={{ color: secondary }}>{subtitle}</div>}
        {tagline && <div className="text-xs opacity-70" style={{ color: "#ffffff" }}>{tagline}</div>}
        <div className="pt-2 text-left text-xs" style={{ color: primary }}>Company Code:</div>
        <div className="h-8 rounded border text-xs flex items-center px-2" style={{ borderColor: `${primary}55`, color: primary, background: "rgba(255,255,255,0.05)" }}>
          e.g. ABC
        </div>
        <div
          className="h-9 rounded flex items-center justify-center text-sm font-semibold"
          style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})`, color: readableOn(primary) }}
        >
          Continue
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Company branding
// ---------------------------------------------------------------------------

export const TenantBrandingEditor: React.FC<{
  tenantId: string;
  tenantName: string;
  initial: Partial<TenantBranding> | null;
  /** Called after a successful save with the new values (the caller applies them to the running app if it is this company). */
  onSaved?: (b: TenantBranding) => void;
}> = ({ tenantId, tenantName, initial, onSaved }) => {
  const [displayName, setDisplayName] = useState("");
  const [tagline, setTagline] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [primary, setPrimary] = useState<string>(DEFAULT_THEME.primary);
  const [secondary, setSecondary] = useState<string>(DEFAULT_THEME.secondary);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDisplayName(initial?.display_name ?? "");
    setTagline(initial?.login_tagline ?? "");
    setLogo(initial?.logo_url ?? null);
    setFavicon(initial?.favicon_url ?? null);
    setPrimary(isHex(initial?.brand_primary) ? initial!.brand_primary! : DEFAULT_THEME.primary);
    setSecondary(isHex(initial?.brand_secondary) ? initial!.brand_secondary! : DEFAULT_THEME.secondary);
  }, [initial, tenantId]);

  const reset = () => {
    setPrimary(DEFAULT_THEME.primary);
    setSecondary(DEFAULT_THEME.secondary);
  };

  const save = async () => {
    setSaving(true);
    try {
      const values = {
        display_name: displayName.trim() || null,
        login_tagline: tagline.trim() || null,
        logo_url: logo,
        favicon_url: favicon,
        brand_primary: primary,
        brand_secondary: secondary,
        brand_background: null,
      };
      await saveTenantBranding(tenantId, values);
      toast({ title: "Branding saved", description: `${displayName.trim() || tenantName} will use these on the login screen and in the app.` });
      onSaved?.({ id: tenantId, tenant_name: tenantName, ...values });
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-4">
        <div>
          <Label>Name shown to your team</Label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={tenantName} className="mt-1" />
          <p className="text-xs text-muted-foreground mt-1">Shown on the login screen and in the browser tab. Leave blank to use "{tenantName}".</p>
        </div>
        <div>
          <Label>Line under the name (optional)</Label>
          <Input value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="e.g. Driver portal" className="mt-1" />
        </div>
        <ImageField label="Logo" hint="Shown above the company name on the login screen. A wide or square PNG works best." value={logo} folder="tenant-logos" onChange={setLogo} />
        <ImageField label="Browser tab icon (favicon)" hint="A small square image, e.g. 256 x 256." value={favicon} folder="tenant-favicons" onChange={setFavicon} />
        <div className="grid grid-cols-2 gap-3">
          <ColourField label="Main text colour" value={primary} onChange={setPrimary} />
          <ColourField label="Second colour" value={secondary} onChange={setSecondary} />
        </div>
        <p className="text-xs text-muted-foreground">
          The main colour is used for your name, text, buttons and highlights. The background is the platform's deep purple on every screen, so
          pick colours that stand out against it. The default is gold. Other screens inside the app pick up the same colours where they use the
          app's standard colour scheme.
        </p>
        {isHex(primary) && contrastRatio(primary, PLATFORM_BACKGROUND.bottom) < 4.5 && (
          <p className="text-xs text-red-500">
            That main colour is hard to read on the purple background. Try a lighter or brighter colour.
          </p>
        )}
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save branding
          </Button>
          <Button type="button" variant="outline" onClick={reset}>
            <RotateCcw className="h-4 w-4 mr-1" /> Reset colours
          </Button>
        </div>
      </div>
      <div>
        <Label>Preview — your login screen</Label>
        <div className="mt-1">
          <LoginPreview
            title={displayName.trim() || tenantName}
            subtitle={tagline.trim() || "DMS"}
            logoUrl={logo}
            primary={primary}
            secondary={secondary}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Platform branding (Superadmin)
// ---------------------------------------------------------------------------

export const PlatformBrandingEditor: React.FC<{ initial: PlatformBranding; onSaved?: (b: PlatformBranding) => void }> = ({ initial, onSaved }) => {
  const [name, setName] = useState(initial.product_name);
  const [subtitle, setSubtitle] = useState(initial.product_subtitle ?? "");
  const [tagline, setTagline] = useState(initial.tagline ?? "");
  const [logo, setLogo] = useState<string | null>(initial.logo_url);
  const [favicon, setFavicon] = useState<string | null>(initial.favicon_url);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(initial.product_name);
    setSubtitle(initial.product_subtitle ?? "");
    setTagline(initial.tagline ?? "");
    setLogo(initial.logo_url);
    setFavicon(initial.favicon_url);
  }, [initial]);

  const save = async () => {
    if (!name.trim()) {
      toast({ title: "Enter the product name", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const values: PlatformBranding = {
        product_name: name.trim(),
        product_subtitle: subtitle.trim() || null,
        tagline: tagline.trim() || null,
        logo_url: logo,
        favicon_url: favicon,
      };
      await savePlatformBranding(values);
      toast({ title: "Platform branding saved", description: "This is what everyone sees before they enter a company code." });
      onSaved?.(values);
    } catch (e: any) {
      toast({ title: "Couldn't save", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-4">
        <div>
          <Label>Product name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={DEFAULT_PLATFORM.product_name} className="mt-1" />
        </div>
        <div>
          <Label>Subtitle</Label>
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder={DEFAULT_PLATFORM.product_subtitle ?? ""} className="mt-1" />
        </div>
        <div>
          <Label>Tagline (optional)</Label>
          <Input value={tagline} onChange={(e) => setTagline(e.target.value)} className="mt-1" />
        </div>
        <ImageField label="Logo" value={logo} folder="platform" onChange={setLogo} />
        <ImageField label="Browser tab icon (favicon)" value={favicon} folder="platform" onChange={setFavicon} />
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Save platform branding
        </Button>
      </div>
      <div>
        <Label>Preview — the screen before a company code is entered</Label>
        <div className="mt-1">
          <LoginPreview
            title={name.trim() || DEFAULT_PLATFORM.product_name}
            subtitle={subtitle.trim() || null}
            tagline={tagline.trim() || null}
            logoUrl={logo}
            primary={PLATFORM_THEME.primary}
            secondary={PLATFORM_THEME.secondary}
          />
        </div>
      </div>
    </div>
  );
};
